import React from 'react';
import { Link } from 'react-router-dom';
import {
  Boxes,
  CalendarX2,
  Check,
  Droplets,
  Layers,
  PhoneMissed,
  UserX,
  Users,
  Wallet,
} from 'lucide-react';
import Alt6Layout from '../components/Alt6Layout';
import AutopilotHero from '../components/AutopilotHero';
import {
  CtaBand,
  DemoCta,
  Eyebrow,
  Reveal,
  SectionHead,
  TextLink,
} from '../components/Alt6Shared';
import { PAIN_POINTS, AUTOPILOT_CHECKLIST } from '../data/site';

const PAIN_ICONS = [PhoneMissed, Users, Droplets, CalendarX2, UserX, Boxes];

export default function Alt6Home() {
  return (
    <Alt6Layout title="The AI-native operating system for dental">
      {/* Hero */}
      <section className="a6-hero">
        <div className="a6-container a6-hero-inner">
          <Reveal>
            <Eyebrow>The AI-native operating system for dental</Eyebrow>
          </Reveal>
          <Reveal delay={0.06}>
            <h1>
              The practice that <em>runs itself</em>
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="a6-hero-sub">
              Asha is a complete practice management system with an AI workforce built
              in — answering every call, verifying insurance, filing claims, posting
              payments, filling the schedule, and following up with patients. On
              autopilot, around the clock.
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="a6-hero-ctas">
              <DemoCta />
              <TextLinkButton />
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <AutopilotHero />
          </Reveal>
        </div>
      </section>

      {/* Problem */}
      <section className="a6-section a6-wash">
        <div className="a6-container">
          <SectionHead
            eyebrow="The problem"
            title="Running a practice shouldn't feel like firefighting"
            lede="Most offices run on heroics — a great office manager, a lucky staffing month, a weekend spent catching up on claims. The work below never stops, and it never should have been manual."
          />
          <div className="a6-grid a6-grid-3">
            {PAIN_POINTS.map((point, i) => {
              const Icon = PAIN_ICONS[i % PAIN_ICONS.length];
              return (
                <Reveal key={point.title} delay={(i % 3) * 0.07}>
                  <div className="a6-card">
                    <span className="a6-card-icon">
                      <Icon size={20} strokeWidth={2.2} />
                    </span>
                    <h3>{point.title}</h3>
                    <p>{point.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Assistance vs autopilot */}
      <section className="a6-section">
        <div className="a6-container">
          <Reveal className="a6-band">
            <Eyebrow>The difference</Eyebrow>
            <h2 className="a6-h2">Assistance isn't autopilot.</h2>
            <p className="a6-lede">
              Most dental AI is a chat window — it gives your team answers they still
              have to act on. Asha's AI does the acting.
            </p>
            <div className="a6-vs">
              <div className="a6-vs-card">
                <div className="a6-vs-tag">Everyone else</div>
                <h3>AI that suggests</h3>
                <p>
                  A chatbot can't answer your phones, verify a patient's benefits, file
                  a claim, work a denial, or collect a balance. It can only tell your
                  team to — and your team is already underwater.
                </p>
              </div>
              <div className="a6-vs-card is-hot">
                <div className="a6-vs-tag">Asha</div>
                <h3>AI that does</h3>
                <p>
                  Calls answered, benefits verified, claims filed, payments posted,
                  schedules filled, patients followed up — real actions completed in
                  the real system of record, end to end, with your rules deciding what
                  needs a human first.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Platform */}
      <section className="a6-section a6-wash">
        <div className="a6-container">
          <div className="a6-split">
            <Reveal className="a6-split-copy">
              <Eyebrow>One platform</Eyebrow>
              <h2>Everything your office runs on, in one system</h2>
              <p>
                Asha replaces the patchwork — practice management, phones, forms,
                imaging, claims, payments, messaging — with one system that shares one
                patient record. That's what makes true autopilot possible: the AI works
                in the same system your team does.
              </p>
              <ul className="a6-split-list">
                {[
                  ['Scheduling, charting & ledgers', 'the full PMS core'],
                  ['Phones, texting & fax', 'every conversation in the record'],
                  ['Claims, payments & analytics', 'the whole revenue cycle, one login'],
                ].map(([strong, rest]) => (
                  <li key={strong}>
                    <Check size={18} strokeWidth={2.6} />
                    <span>
                      <strong>{strong}</strong> — {rest}
                    </span>
                  </li>
                ))}
              </ul>
              <TextLink to="/alt6/platform">Explore the platform</TextLink>
            </Reveal>
            <Reveal delay={0.1}>
              <ConsolidationGraphic />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Autopilot outcomes */}
      <section className="a6-section">
        <div className="a6-container">
          <SectionHead
            eyebrow="On autopilot"
            title="You'll know it's working when…"
            lede="We call it the automation inflection point — when enough of the practice runs itself that these three things are simply true."
            centered
          />
          <div className="a6-checklist">
            {AUTOPILOT_CHECKLIST.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.08}>
                <div className="a6-check-card">
                  <span className="a6-check-mark">
                    <Check size={17} strokeWidth={3} />
                  </span>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p style={{ textAlign: 'center', marginTop: 36 }}>
              <TextLink to="/alt6/autopilot">How autopilot works</TextLink>
            </p>
          </Reveal>
        </div>
      </section>

      {/* Journey teaser */}
      <section className="a6-section a6-wash">
        <div className="a6-container">
          <div className="a6-split">
            <Reveal>
              <JourneyMini />
            </Reveal>
            <Reveal className="a6-split-copy" delay={0.1}>
              <Eyebrow>End to end</Eyebrow>
              <h2>One patient, zero dropped handoffs</h2>
              <p>
                From the first after-hours call to the recall visit six months later,
                every step of the patient journey is carried by the same system — no
                sticky notes, no "did anyone call them back?", no revenue aging in a
                folder.
              </p>
              <TextLink to="/alt6/patient-journey">Follow the patient journey</TextLink>
            </Reveal>
          </div>
        </div>
      </section>

      <CtaBand />
    </Alt6Layout>
  );
}

function TextLinkButton() {
  return (
    <Link className="a6-btn a6-btn-ghost" to="/alt6/autopilot">
      See how autopilot works
    </Link>
  );
}

function ConsolidationGraphic() {
  const modules = [
    'PMS core',
    'Phones & VoIP',
    'Texting & inbox',
    'Digital forms',
    'Online booking',
    'Cloud imaging',
    'eClaims',
    'Payments',
    'Patient portal',
    'ePrescribe',
    'Smart fax',
    'Analytics',
  ];
  return (
    <div className="a6-sim" style={{ marginTop: 0 }} aria-hidden="true">
      <div className="a6-sim-chrome">
        <span className="a6-sim-dots">
          <span />
          <span />
          <span />
        </span>
        <span className="a6-sim-title">One login · one patient record</span>
        <span className="a6-sim-live">
          <Layers size={12} strokeWidth={2.6} />
          12 modules
        </span>
      </div>
      <div style={{ padding: 22, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        {modules.map((m) => (
          <div
            key={m}
            className="a6-slot"
            style={{ justifyContent: 'center', textAlign: 'center', padding: '14px 8px' }}
          >
            <span className="a6-slot-patient" style={{ fontWeight: 600, color: 'var(--a6-ink)' }}>
              {m}
            </span>
          </div>
        ))}
      </div>
      <div className="a6-sim-footer">
        <span className="a6-sim-loop">
          <Wallet size={14} strokeWidth={2.4} />
          One vendor
        </span>
        <span>— instead of a stack of tools that don't talk to each other.</span>
      </div>
    </div>
  );
}

function JourneyMini() {
  const steps = [
    ['8:45 PM', 'New patient call answered & booked'],
    ['Next day', 'Intake charted · benefits verified'],
    ['Visit', 'Notes written while the provider stays chairside'],
    ['Same day', 'Claim scrubbed & submitted'],
    ['2 weeks', 'EOB posted · ledger reconciled'],
    ['6 months', 'Recall outreach brings them back'],
  ];
  return (
    <div className="a6-sim" style={{ marginTop: 0 }} aria-hidden="true">
      <div className="a6-sim-chrome">
        <span className="a6-sim-dots">
          <span />
          <span />
          <span />
        </span>
        <span className="a6-sim-title">Patient journey · handled by Asha</span>
      </div>
      <div style={{ padding: 22 }}>
        {steps.map(([when, what]) => (
          <div className="a6-slot" key={when} style={{ marginTop: 0, marginBottom: 9 }}>
            <span className="a6-slot-time" style={{ width: 74 }}>
              {when}
            </span>
            <span className="a6-slot-patient">{what}</span>
            <span className="a6-slot-pill a6-pill-green">
              <Check size={11} strokeWidth={3} style={{ verticalAlign: '-1px' }} /> Done
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
